import TableIcon from '@material-ui/icons/GridOn';
import FormIcon from '@material-ui/icons/Tune';
import APIDocIcon from '@material-ui/icons/Code';
import TutorialDocIcon from '@material-ui/icons/Subject';

import App, { AppMain } from '../components/App';
import NavDrawer from '../components/NavDrawer';
import DrawerHeader from '../components/App/DrawerHeader';
import AppToolbar from '../components/AppToolbar';
import TableViewComponent, { BodyRow } from '../components/TableViewComponent';
import FormViewComponent from '../components/FormViewComponent';
import ApiComponent from '../components/Api';
import SearchBar from '../components/SearchBar';

import RouteService from '../services/RouteService';
import SchemaService from '../services/SchemaService';
import AuthService from '../services/AuthService';
import {
  ViewService,
  APP_TYPE,
  APP_MAIN_TYPE,
  APP_NAV_DRAWER_TYPE,
  APP_NAV_DRAWER_HEADER_TYPE,
  APP_TOOLBAR_TYPE,
  TABLE_VIEW_TYPE,
  TABLE_ROW_TYPE,
  FORM_VIEW_TYPE,
  DOCUMENTATION_API_VIEW_TYPE,
  SEARCH_BAR_TYPE,
} from '../services/ViewService';

class Configuration {
  constructor({ plugins, ...params }) {
    this.schema = params.schema;
    this.jsonConfig = params.jsonConfig;
    this.plugins = plugins && plugins.map(PluginClass => new PluginClass(params)) || [];
    this.onError = this.onError.bind(this);
    this.initializePlugins(params);
    this.createServices(params, this.plugins);
    // Process JSON configuration, including process data according to schema service,
    // process component configuration, etc...
    this.processJSONConfiguration();
    // Routes initialization
    this.routeService = this.createService(RouteService, params, this.plugins, 'createRouteService');
    this.services.routeService = this.routeService;
    this.initializeRoutes(params);
    // Configuration and its services has been initializes
    // Invoke plugins to let perform actions with initialized services.
    this.configurationInitialized(params);
  }

  createService(Class, props, plugins, pluginCreateMethodName) {
    for (let i = plugins.length - 1; i >= 0; i -= 1) {
      if (plugins[i][pluginCreateMethodName]) {
        return plugins[i][pluginCreateMethodName](this, props);
      }
    }
    return new Class(this, props);
  }

  createServices(params, plugins) {
    this.schemaService = this.createService(SchemaService, params, plugins, 'createSchemaService');
    this.viewService = this.createService(ViewService, params, plugins, 'createViewService');
    this.authService = this.createService(AuthService, params, plugins, 'createAuthService');
    this.services = {
      schemaService: this.schemaService,
      viewService: this.viewService,
      authService: this.authService,
    };
    this.plugins.forEach((plugin) => {
      const services = plugin.createServices(this, this.services);
      if (services) {
        Object.assign(this.services, services);
        Object.assign(this, services);
      }
    });
    this.registerComponents(this.viewService);
    this.registerIcons(this.viewService);
    this.registerValidators(this.viewService, params);
    this.registerFormatters(this.viewService, params);
    this.registerEditors(this.viewService, params);
    this.registerComputedProperties(this.viewService, params);
    this.registerViewHooks(this.viewService, params);
  }

  initializePlugins(props) {
    this.plugins.forEach(plugin => plugin.initialize(this, props));
  }

  processJSONConfiguration() {
    let { jsonConfig } = this;
    this.plugins.forEach((plugin) => {
      if (plugin.processJSONConfiguration) {
        jsonConfig = plugin.processJSONConfiguration(jsonConfig);
      }
    });
    this.jsonConfig = jsonConfig;
  }

  initializeRoutes(props) {
    this.plugins.forEach(plugin => plugin.initializeRoutes(this.routeService, this, props));
  }

  getServices() {
    // Object.values less supported
    return Object.keys(this.services).map(name => this.services[name]);
  }

  configurationInitialized(props) {
    this.plugins.forEach(plugin => plugin.configurationInitialized(this, props));
    this.getServices().forEach((service) => {
      if (service.configurationInitialized) {
        service.configurationInitialized(this);
      }
    });
  }

  getViewService() {
    return this.viewService;
  }

  getDataService() {
    return this.dataService;
  }

  getScenarioService() {
    return this.scenarioService;
  }

  getRouteService() {
    return this.routeService;
  }

  getRoutes() {
    return this.routeService.getRoutes(this.jsonConfig,
      this.config, this.viewService, this.schemaService, this.dataService, this.routeService.onError);
  }

  getApplicationTitle() {
    return this.applicationTitle;
  }

  getNav() {
    return this.jsonConfig && this.jsonConfig.nav || {};
  }

  setApplicationTitle(title) {
    this.applicationTitle = title;
  }

  getPrefix() {
    return this.prefix;
  }

  setPrefix(prefix) {
    this.prefix = prefix;
  }

  onError(err) {
    if (this.viewService && this.viewService.onError) {
      this.viewService.onError(err);
    } else if (console.error) {
      console.error(err);
    } else {
      console.log(err);
    }
  }

  clean() {
    this.jsonConfig = null;
    // Bar chart initializes formatters at runtime
    // this.viewService.clearFormattersAndValidators();
  }

  registerComponents(viewService) {
    viewService.registerComponent(APP_TYPE, App);
    viewService.registerComponent(APP_NAV_DRAWER_TYPE, NavDrawer);
    viewService.registerComponent(APP_NAV_DRAWER_HEADER_TYPE, DrawerHeader);
    viewService.registerComponent(APP_MAIN_TYPE, AppMain);
    viewService.registerComponent(APP_TOOLBAR_TYPE, AppToolbar);
    viewService.registerViewComponent(TABLE_VIEW_TYPE, {
      icon: TableIcon,
      component: TableViewComponent,
      multiData: true,
    });
    viewService.registerComponent(TABLE_ROW_TYPE, BodyRow);
    viewService.registerViewComponent(FORM_VIEW_TYPE, {
      icon: FormIcon,
      component: FormViewComponent,
      multiData: false,
    });
    viewService.registerViewComponent(DOCUMENTATION_API_VIEW_TYPE, {
      icon: APIDocIcon,
      component: ApiComponent,
      multiData: false,
    });
    viewService.registerComponent(SEARCH_BAR_TYPE, SearchBar);
    this.plugins.forEach(plugin => plugin.registerComponents(viewService));
  }

  registerIcons(viewService) {
    this.plugins.forEach(plugin => plugin.registerIcons(viewService));
  }

  registerValidators(viewService, props) {
    // Register validators by name
    this.plugins.forEach(plugin => plugin.registerValidators(viewService, this, props));
    // Register validators declared in the 'views.json' configuration file.
    viewService.registerValidators(this, props);
  }

  registerFormatters(viewService, props) {
    // Register formatters by name
    this.plugins.forEach(plugin => plugin.registerFormatters(viewService, this, props));
    // Register formatters declared in the 'views.json' configuration file.
    viewService.registerFormatters(this, props);
  }

  registerEditors(viewService, props) {
    this.plugins.forEach(plugin => plugin.registerEditors(viewService, this, props));
  }

  registerComputedProperties(viewService, props) {
    this.plugins.forEach(plugin => plugin.registerComputedProperties(viewService, this, props));
  }

  registerViewHooks(viewService, props) {
    this.plugins.forEach(plugin => plugin.registerViewHooks(viewService, this, props));
  }
}

export default Configuration;
